import type {} from '@react-three/fiber';
import { PROMOTION_RANK } from '@strategy4kids/game-engine';
import type { Turn } from '@strategy4kids/protocol';
import * as THREE from 'three';

/**
 * Checkers promotion row — the band behind the rank where a man becomes a king.
 *
 * **A band, not squares.** Every other overlay on this board is per-square:
 * highlight rings, the power wash, the shield dots. The crown row is a *zone* —
 * the whole far edge means one thing — so it renders as one row-wide plane
 * instead of eight tinted squares. That shape difference is what keeps it apart
 * from the highlight vocabulary without spending a hue: gold reads as "crown",
 * the band shape reads as "this whole edge", and neither collides with
 * green/red/amber/cyan (moves), violet (power) or grape/berry (shields).
 *
 * **Static, deliberately not pulsing.** A slow opacity pulse was considered and
 * dropped: visual-regression goldens screenshot the board at arbitrary frames,
 * so an animated overlay makes every checkers-board capture a coin flip (see
 * the marketing-page two-variants flake for what that turns into). A steady
 * low-alpha band says the same thing and captures byte-stable.
 *
 * **Height is the whole trick**, same lesson as `PowerMap`: this is the lowest
 * overlay on the board, because it is context, not signal — the wash, rings and
 * arrows a kid is actively reading must all draw above it.
 */

export const CROWN_TINT = new THREE.MeshBasicMaterial({
  color: '#e8b13f',
  transparent: true,
  opacity: 0.16,
  depthWrite: false,
});

/** One shared plane for the whole row — slightly wider than the board, so the
 *  band reads as the edge itself rather than eight tiles with seams. */
const bandPlane = new THREE.PlaneGeometry(8.12, 0.9);

/**
 * The band height, exported for the same reason as `POWER_PLANE_Y`: the
 * below-everything claim is asserted in a test rather than trusted to prose.
 */
export const PROMOTION_BAND_Y = 0.012;

/**
 * Crown rank → world z, the same conversion `squareToVec3` does per square:
 * rank 8 → 3.5 (white's goal, the far edge), rank 1 → -3.5. Exported because
 * the rank→z flip is the one claim in this module a UI could get backwards with
 * no type error, and this package tests data, not JSX.
 */
export function promotionBandZ(color: Turn): number {
  return PROMOTION_RANK[color] - 4.5;
}

/**
 * The crown row for `color`, as one band. Purely coordinate-derived — the FEN
 * does not matter, because a man crowns by *arriving*, so the row is marked
 * even while it is empty. That is the point: the goal is visible from move one.
 */
export function PromotionBand({ color }: { color: Turn }) {
  return (
    <mesh
      geometry={bandPlane}
      position={[0, PROMOTION_BAND_Y, promotionBandZ(color)]}
      rotation={[-Math.PI / 2, 0, 0]}
      material={CROWN_TINT}
    />
  );
}
